import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wrench,
  ChevronUp,
  ChevronDown,
  ExternalLink,
  X,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { usePostStats } from '@/context/PostStatsContext';
import { fetchPublicSiteConfig, type SiteConfig } from '@/api/site-config.api';
import { useEffectsStore } from '@/store/effects.store.ts';
import {
  clampMobileAnchor,
  MOBILE_BTN_SIZE,
  MOBILE_EDGE_GAP,
  MOBILE_HALF_RING_SPAN,
  MOBILE_RING_RADIUS,
  normalizeAngleDiff,
} from './floating-toolbar.utils';
import {
  createInteractionActions,
  createMainActions,
  createUtilityActions,
} from './floating-toolbar.actions';

interface FloatingToolbarProps {
  className?: string;
  onOpenTheme?: () => void;
  onOpenEffects?: () => void;
}

const FloatingToolbar: React.FC<FloatingToolbarProps> = ({ className = '', onOpenTheme, onOpenEffects }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [mobileDormant, setMobileDormant] = useState(false);
  const mobileIdleTimerRef = useRef<number | null>(null);
  const [mobileAnchor, setMobileAnchor] = useState(() => ({
    x: window.innerWidth - MOBILE_EDGE_GAP - MOBILE_BTN_SIZE / 2,
    y: window.innerHeight / 2,
  }));
  const [orbitRotation, setOrbitRotation] = useState(0);
  const pointerRef = useRef({
    active: false,
    pointerId: -1,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    moved: false,
  });
  const skipClickRef = useRef(false);
  const orbitGestureRef = useRef<{ lastAngle: number } | null>(null);

  const { postStats, setPostStats } = usePostStats();
  const location = useLocation();
  const isPostPage = location.pathname.startsWith('/post/');
  const applySiteEffectsConfig = useEffectsStore((state) => state.applySiteEffectsConfig);

  useEffect(() => {
    let cancelled = false;
    fetchPublicSiteConfig().then((config) => {
      if (!cancelled) {
        setSiteConfig(config);
        applySiteEffectsConfig(config.effects);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [applySiteEffectsConfig]);

  const supportConfig = useMemo(() => siteConfig?.supportAuthor, [siteConfig]);

  const mainActions = useMemo(
    () => createMainActions({ siteConfig, onOpenTheme }),
    [siteConfig, onOpenTheme],
  );

  const interactionActions = useMemo(
    () => createInteractionActions({ postStats, setPostStats }),
    [postStats, setPostStats],
  );

  const utilityActions = useMemo(
    () =>
      createUtilityActions({
        onOpenEffects,
        effectsEnabled: siteConfig?.effects?.enabled !== false,
        supportEnabled: supportConfig?.enabled === true,
        setIsExpanded,
        setSupportOpen,
      }),
    [onOpenEffects, siteConfig?.effects?.enabled, supportConfig?.enabled],
  );

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const updateMobile = () => {
      const nextMobile = media.matches;
      setIsMobile(nextMobile);
      if (nextMobile) {
        setMobileAnchor((prev) => {
          const hasCustom = prev.x > 0 && prev.y > 0;
          if (!hasCustom) {
            return {
              x: window.innerWidth - MOBILE_EDGE_GAP - MOBILE_BTN_SIZE / 2,
              y: window.innerHeight / 2,
            };
          }
          return clampMobileAnchor(prev.x, prev.y);
        });
      }
    };
    updateMobile();
    const handler = () => updateMobile();
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      if (mobileIdleTimerRef.current) {
        window.clearTimeout(mobileIdleTimerRef.current);
        mobileIdleTimerRef.current = null;
      }
      setMobileDormant(false);
      return;
    }
    if (isExpanded) {
      setMobileDormant(false);
      if (mobileIdleTimerRef.current) {
        window.clearTimeout(mobileIdleTimerRef.current);
        mobileIdleTimerRef.current = null;
      }
      return;
    }
    if (mobileIdleTimerRef.current) {
      window.clearTimeout(mobileIdleTimerRef.current);
    }
    mobileIdleTimerRef.current = window.setTimeout(() => {
      setMobileDormant(true);
    }, 3800);

    return () => {
      if (mobileIdleTimerRef.current) {
        window.clearTimeout(mobileIdleTimerRef.current);
        mobileIdleTimerRef.current = null;
      }
    };
  }, [isExpanded, isMobile, mobileAnchor.x, mobileAnchor.y]);

  useEffect(() => {
    const onResize = () => {
      if (!isMobile) return;
      setMobileAnchor((prev) => {
        const clamped = clampMobileAnchor(prev.x, prev.y);
        const dockRight = clamped.x >= window.innerWidth / 2;
        return {
          x: dockRight
            ? window.innerWidth - MOBILE_EDGE_GAP - MOBILE_BTN_SIZE / 2
            : MOBILE_EDGE_GAP + MOBILE_BTN_SIZE / 2,
          y: clamped.y,
        };
      });
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isMobile]);

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      if (isExpanded) return;
      if (!pointerRef.current.active || event.pointerId !== pointerRef.current.pointerId) return;
      const dx = event.clientX - pointerRef.current.startX;
      const dy = event.clientY - pointerRef.current.startY;
      if (!pointerRef.current.moved && Math.hypot(dx, dy) > 6) {
        pointerRef.current.moved = true;
      }
      if (!pointerRef.current.moved) return;

      const next = clampMobileAnchor(pointerRef.current.originX + dx, pointerRef.current.originY + dy);
      setMobileAnchor(next);
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
    };

    const onPointerUp = (event: PointerEvent) => {
      if (!pointerRef.current.active || event.pointerId !== pointerRef.current.pointerId) return;
      const moved = pointerRef.current.moved;
      pointerRef.current.active = false;
      pointerRef.current.pointerId = -1;
      pointerRef.current.moved = false;
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';

      if (moved) {
        skipClickRef.current = true;
        setMobileAnchor((prev) => ({
          x: prev.x >= window.innerWidth / 2
            ? window.innerWidth - MOBILE_EDGE_GAP - MOBILE_BTN_SIZE / 2
            : MOBILE_EDGE_GAP + MOBILE_BTN_SIZE / 2,
          y: prev.y,
        }));
      }
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [isExpanded]);

  const mobileActions = useMemo(() => {
    const postOnly = isPostPage ? interactionActions : [];
    return [...mainActions, ...postOnly, ...utilityActions];
  }, [interactionActions, isPostPage, mainActions, utilityActions]);

  const mobileAngles = useMemo(() => {
    const count = mobileActions.length;
    if (count <= 0) return [] as number[];

    if (count <= 5) {
      const dockRight = mobileAnchor.x >= window.innerWidth / 2;
      const centerAngle = dockRight ? 180 : 0;
      if (count === 1) return [centerAngle];
      const start = centerAngle - MOBILE_HALF_RING_SPAN / 2;
      const step = MOBILE_HALF_RING_SPAN / (count - 1);
      return Array.from({ length: count }, (_, index) => start + step * index);
    }

    const step = 360 / count;
    return Array.from({ length: count }, (_, index) => orbitRotation + step * index - 90);
  }, [mobileActions.length, mobileAnchor.x, orbitRotation]);
  const useCircleLayout = mobileActions.length > 5;

  return (
    <>
      {isMobile ? (
        <>
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={`fixed inset-0 z-[64] ${className}`}
                style={{
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  touchAction: 'none',
                  overscrollBehavior: 'contain',
                }}
                onClick={() => setIsExpanded(false)}
                onTouchStart={(event) => {
                  if (!useCircleLayout) return;
                  event.stopPropagation();
                  const touch = event.touches[0];
                  if (!touch) return;
                  const angle = Math.atan2(touch.clientY - mobileAnchor.y, touch.clientX - mobileAnchor.x) * (180 / Math.PI);
                  orbitGestureRef.current = { lastAngle: angle };
                }}
                onTouchMove={(event) => {
                  if (!useCircleLayout) return;
                  event.stopPropagation();
                  const touch = event.touches[0];
                  const state = orbitGestureRef.current;
                  if (!touch || !state) return;
                  const angle = Math.atan2(touch.clientY - mobileAnchor.y, touch.clientX - mobileAnchor.x) * (180 / Math.PI);
                  const delta = normalizeAngleDiff(angle - state.lastAngle);
                  state.lastAngle = angle;
                  setOrbitRotation((prev) => prev + delta);
                }}
                onTouchEnd={() => {
                  orbitGestureRef.current = null;
                }}
              >
                {mobileActions.map((action, index) => {
                  const Icon = action.icon;
                  const angle = (mobileAngles[index] * Math.PI) / 180;
                  const x = mobileAnchor.x + Math.cos(angle) * MOBILE_RING_RADIUS;
                  const y = mobileAnchor.y + Math.sin(angle) * MOBILE_RING_RADIUS;
                  return (
                    <button
                      key={action.id}
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        action.action();
                      }}
                      title={action.label}
                      className={`absolute w-10 h-10 rounded-full ${action.color} text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform`}
                      style={{
                        left: `${x}px`,
                        top: `${y}px`,
                        transform: 'translate(-50%, -50%)',
                      }}
                    >
                      <Icon size={16} className={action.active ? 'fill-current' : ''} />
                      {action.count != null && action.count > 0 ? (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full min-w-4 h-4 px-1 flex items-center justify-center">
                          {action.count > 99 ? '99+' : action.count}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>

          {mobileDormant && !isExpanded ? (
            <button
              type="button"
              onClick={() => setMobileDormant(false)}
              className={`fixed z-[66] h-10 w-6 ${className}`}
              style={{
                left: mobileAnchor.x >= window.innerWidth / 2 ? window.innerWidth - 16 : 0,
                top: mobileAnchor.y - 20,
                backgroundColor: 'var(--accent-color, #2563eb)',
                color: '#fff',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
              title="唤起工具栏"
            >
              <Wrench size={14} className="mx-auto" />
            </button>
          ) : null}

          {!mobileDormant ? (
            <button
              type="button"
              onPointerDown={(event) => {
                if (isExpanded) return;
                setMobileDormant(false);
                if (mobileIdleTimerRef.current) {
                  window.clearTimeout(mobileIdleTimerRef.current);
                  mobileIdleTimerRef.current = null;
                }
                pointerRef.current.active = true;
                pointerRef.current.pointerId = event.pointerId;
                pointerRef.current.startX = event.clientX;
                pointerRef.current.startY = event.clientY;
                pointerRef.current.originX = mobileAnchor.x;
                pointerRef.current.originY = mobileAnchor.y;
                pointerRef.current.moved = false;
              }}
              onClick={() => {
                if (skipClickRef.current) {
                  skipClickRef.current = false;
                  return;
                }
                setMobileDormant(false);
                setIsExpanded((prev) => !prev);
              }}
              className={`fixed z-[66] w-12 h-12 bg-blue-600 hover:brightness-110 text-white rounded-full shadow-xl flex items-center justify-center ${className}`}
              style={{
                left: mobileAnchor.x - MOBILE_BTN_SIZE / 2,
                top: mobileAnchor.y - MOBILE_BTN_SIZE / 2,
                backgroundColor: 'var(--accent-color, #2563eb)',
                touchAction: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
              title={isExpanded ? '收起工具栏' : '展开工具栏'}
            >
              <motion.div animate={{ rotate: isExpanded ? 90 : 0 }} transition={{ duration: 0.2 }}>
                {isExpanded ? <X size={20} /> : <Wrench size={20} />}
              </motion.div>
            </button>
          ) : null}
        </>
      ) : (
        <div className={`fixed left-4 top-1/2 transform -translate-y-1/2 z-40 ${className}`}>
          <div className="flex flex-col items-center space-y-3">
            <motion.button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              title={isExpanded ? '收起工具栏' : '展开工具栏'}
            >
              <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.3 }}>
                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </motion.div>
            </motion.button>

            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, staggerChildren: 0.05 }}
                  className="flex flex-col items-center space-y-2"
                >
                  {mainActions.map((action, index) => {
                    const Icon = action.icon;
                    return (
                      <motion.button
                        key={action.id}
                        type="button"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={action.action}
                        className={`w-10 h-10 ${action.color} text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center group relative`}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        title={action.label}
                      >
                        <Icon size={18} />
                        <div className="absolute left-full ml-3 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                          {action.label}
                        </div>
                      </motion.button>
                    );
                  })}

                  <div className="w-6 h-px bg-gray-300 dark:bg-gray-600 my-1" />

                  {isPostPage &&
                    interactionActions.map((action, index) => {
                      const Icon = action.icon;
                      return (
                        <motion.button
                          key={action.id}
                          type="button"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: (mainActions.length + index) * 0.05 }}
                          onClick={action.action}
                          className={`w-10 h-10 ${action.color} text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center group relative`}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          title={action.label}
                        >
                          <Icon size={18} className={action.active ? 'fill-current' : ''} />
                          {action.count != null && action.count > 0 && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                              {action.count > 99 ? '99+' : action.count}
                            </span>
                          )}
                          <div className="absolute left-full ml-3 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                            {action.label}
                            {action.count ? ` (${action.count})` : ''}
                          </div>
                        </motion.button>
                      );
                    })}

                  {isPostPage && <div className="w-6 h-px bg-gray-300 dark:bg-gray-600 my-1" />}

                  {utilityActions.map((action, index) => {
                    const Icon = action.icon;
                    return (
                      <motion.button
                        key={action.id}
                        type="button"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          delay:
                            (mainActions.length + (isPostPage ? interactionActions.length : 0) + index) *
                            0.05,
                        }}
                        onClick={action.action}
                        className={`w-10 h-10 ${action.color} text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center group relative`}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        title={action.label}
                      >
                        <Icon size={18} />
                        <div className="absolute left-full ml-3 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                          {action.label}
                        </div>
                      </motion.button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      <AnimatePresence>
        {supportOpen && supportConfig?.enabled && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] bg-black/40"
              onClick={() => setSupportOpen(false)}
            />
            <div className="fixed inset-0 z-[71] flex items-center justify-center p-4 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, y: 16, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 16, scale: 0.98 }}
                className="pointer-events-auto w-[420px] max-w-[calc(100vw-2rem)] bg-[var(--bg-card)] border border-[var(--border-light)] shadow-xl p-4"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h3 className="text-base font-semibold text-[var(--text-primary)]">
                      {supportConfig.title || '支持作者'}
                    </h3>
                    <p className="text-sm text-[var(--text-secondary)] mt-1">
                      {supportConfig.description || '感谢你的支持'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSupportOpen(false)}
                    className="p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  >
                    <X size={16} />
                  </button>
                </div>

                {supportConfig.qrImage ? (
                  <img
                    src={supportConfig.qrImage}
                    alt="支持作者二维码"
                    className="w-full border border-[var(--border-light)] mb-3"
                  />
                ) : null}

                {supportConfig.link ? (
                  <a
                    href={supportConfig.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                  >
                    <ExternalLink size={14} />
                    前往支持页面
                  </a>
                ) : null}
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingToolbar;
